import { useCallback } from 'react'

import { useAppLanguage } from '@/atoms'
import { useIntl } from '@/intl'
import { createIntl } from '@/local/server-data/utils/intl'

import { type Column, OrderDataStatus } from './useController'

type Suffix = '_execution' | '_lng' | '_lat' | '_time' | '_value' | '_status'

const STANDARD_SUFFIXES: Suffix[] = ['_execution', '_lng', '_lat', '_time', '_value', '_status']

const TRANSLATION_KEYS = {
  COLUMN_PREFIX: 'modals.exportOrders.column.',
  SUFFIX_PREFIX: 'modals.exportOrders.column.suffix.',
} as const

type ColumnType = 'customField' | 'barcode' | 'load'
type PrefixConfig = {
  prefixes: string[]
  getTranslationId: (prefix: string) => string
  fallbackTranslationId?: string
}

const PREFIX_CONFIGS: Record<ColumnType, PrefixConfig> = {
  customField: {
    prefixes: ['CF_D_', 'CF_P_', 'CF_S_'],
    getTranslationId: (prefix: string): string => {
      switch (prefix) {
        case 'CF_D_':
          return `${TRANSLATION_KEYS.COLUMN_PREFIX}customFields.delivery`
        case 'CF_P_':
          return `${TRANSLATION_KEYS.COLUMN_PREFIX}customFields.pickup`
        case 'CF_S_':
          return `${TRANSLATION_KEYS.COLUMN_PREFIX}customFields.service`
        default:
          throw new Error(`Unrecognized custom field prefix: ${prefix}`)
      }
    },
  },
  barcode: {
    prefixes: ['BC_D_', 'BC_P_', 'BC_S_'],
    getTranslationId: (prefix: string): string => {
      switch (prefix) {
        case 'BC_D_':
          return `${TRANSLATION_KEYS.COLUMN_PREFIX}barcodes.delivery`
        case 'BC_P_':
          return `${TRANSLATION_KEYS.COLUMN_PREFIX}barcodes.pickup`
        case 'BC_S_':
          return `${TRANSLATION_KEYS.COLUMN_PREFIX}barcodes.service`
        default:
          throw new Error(`Unrecognized barcode prefix: ${prefix}`)
      }
    },
  },
  load: {
    prefixes: ['L_D_', 'L_P_', 'L_S_', 'L_'],
    getTranslationId: (prefix: string): string => {
      switch (prefix) {
        case 'L_D_':
          return `${TRANSLATION_KEYS.COLUMN_PREFIX}loads.delivery`
        case 'L_P_':
          return `${TRANSLATION_KEYS.COLUMN_PREFIX}loads.pickup`
        case 'L_S_':
          return `${TRANSLATION_KEYS.COLUMN_PREFIX}loads.service`
        default:
          return `${TRANSLATION_KEYS.COLUMN_PREFIX}loads`
      }
    },
    fallbackTranslationId: `${TRANSLATION_KEYS.COLUMN_PREFIX}loads`,
  },
}

function parseColumnValue(
  value: string,
  orderDataStatus: OrderDataStatus,
  columnType: ColumnType,
): [string, string, string?] {
  const { prefixes } = PREFIX_CONFIGS[columnType]

  if (orderDataStatus === 'configuration') {
    for (const prefix of prefixes) {
      if (value.startsWith(prefix)) {
        return [value.slice(prefix.length), prefix]
      }
    }
  } else {
    for (const prefix of prefixes) {
      if (value.startsWith(prefix)) {
        const suffix = STANDARD_SUFFIXES.find(s => value.endsWith(s))
        if (suffix) {
          return [value.slice(prefix.length, -suffix.length), prefix, suffix]
        }
        return [value.slice(prefix.length), prefix]
      }
    }
  }

  throw new Error(`Cannot find valid prefix for ${columnType} value: ${value}`)
}

function getTranslationIdForPrefix(prefix: string, columnType: ColumnType): string {
  return PREFIX_CONFIGS[columnType].getTranslationId(prefix)
}

function safeTranslate(
  translate: uui.domain.intl.Translate,
  id: string,
  values?: Record<string, any>,
): string {
  return translate({
    id: id as uui.domain.intl.LocalizationIdentifier,
    values,
  })
}

export function useCreateColumns() {
  const { translate: enTranslate } = useIntl()
  const [{ language }] = useAppLanguage()

  return useCallback(
    async (
      defs: uui.domain.actions.rpc.order.ExportOrderColumnsDef,
      keepEnglishColumns: boolean,
      orderDataStatus: OrderDataStatus,
    ) => {
      try {
        const columns: Column[] = []

        const translate = await createLocalTranslate(keepEnglishColumns, language, enTranslate)
        if (!translate) {
          throw new Error('cannot compte translate function')
        }
        for (const c of defs.columns) {
          columns.push({
            id: c,
            label: safeTranslate(translate, `${TRANSLATION_KEYS.COLUMN_PREFIX}${c}`),
          })
        }

        if (defs.cfs) {
          for (const c of defs.cfs) {
            const [value, prefix, suffix] = parseColumnValue(c, orderDataStatus, 'customField')
            const translationId = getTranslationIdForPrefix(prefix, 'customField')

            columns.push({
              id: c,
              label: suffix
                ? safeTranslate(translate, translationId, { value }) +
                  ' ' +
                  safeTranslate(translate, `${TRANSLATION_KEYS.SUFFIX_PREFIX}${suffix}`)
                : safeTranslate(translate, translationId, { value }),
            })
          }
        }

        if (defs.barcodes) {
          for (const c of defs.barcodes) {
            const [value, prefix, suffix] = parseColumnValue(c, orderDataStatus, 'barcode')
            const translationId = getTranslationIdForPrefix(prefix, 'barcode')

            columns.push({
              id: c,
              label: suffix
                ? safeTranslate(translate, translationId, { value }) +
                  ' ' +
                  safeTranslate(translate, `${TRANSLATION_KEYS.SUFFIX_PREFIX}${suffix}`)
                : safeTranslate(translate, translationId, { value }),
            })
          }
        }

        if (defs.loads) {
          for (const c of defs.loads) {
            const [value, prefix, suffix] = parseColumnValue(c, orderDataStatus, 'load')
            const translationId =
              orderDataStatus === 'configuration'
                ? `${TRANSLATION_KEYS.COLUMN_PREFIX}loads`
                : getTranslationIdForPrefix(prefix, 'load')

            columns.push({
              id: c,
              label: suffix
                ? safeTranslate(translate, translationId, { value }) +
                  ' ' +
                  safeTranslate(translate, `${TRANSLATION_KEYS.SUFFIX_PREFIX}${suffix}`)
                : safeTranslate(translate, translationId, { value }),
            })
          }
        }

        columns.sort((a, b) => a.label.localeCompare(b.label))

        return columns
      } catch (e) {
        console.error(e)
        return [] as Column[]
      }
    },
    [language, enTranslate],
  )
}

async function createLocalTranslate(
  keepEnglishColumns: boolean,
  language: uui.domain.Language,
  translate: uui.domain.intl.Translate,
) {
  try {
    if (!keepEnglishColumns) return translate

    if (language === 'en') return translate

    const intl = await createIntl('en')

    await intl.ready

    return intl.translate
  } catch (e) {
    console.error(e)
  }
}
